
import React, { useState } from 'react';
import { Website } from '../types';
import { Icons } from '../constants';
import { deleteFromR2 } from '../services/storage';
import { WebsiteCreator } from '../components/WebsiteCreator';

interface WebsitesPageProps {
  websites: Website[];
  onSelectSite: (site: Website) => void;
  onAddSite: (site: Website) => void;
  onDeleteSite?: (id: string) => void;
}

export const Websites: React.FC<WebsitesPageProps> = ({ websites, onSelectSite, onAddSite, onDeleteSite }) => {
  const [isCreatorOpen, setIsCreatorOpen] = useState(false);

  const handleCreate = (newSite: Website) => {
    onAddSite(newSite);
    setIsCreatorOpen(false);
    onSelectSite(newSite);
  };

  const handleDelete = async (e: React.MouseEvent, site: Website) => {
    e.stopPropagation();
    if (!window.confirm(`PERMANENT ACTION: Are you sure you want to delete "${site.name}"?`)) return;
    try {
      await deleteFromR2({ prefix: `${site.tenantId}/${site.id}/` });
      if (onDeleteSite) onDeleteSite(site.id);
    } catch (err) {
      alert("Error during deletion");
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Websites</h1>
          <p className="text-slate-500 text-sm">Design, build and manage high-conversion landing pages.</p>
        </div>
        <button 
          onClick={() => setIsCreatorOpen(true)}
          className="bg-indigo-600 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 flex items-center gap-2"
        >
          <Icons.Plus /> Create New Website
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {websites.map((site) => (
          <div key={site.id} className="bg-white rounded-3xl border border-slate-200 overflow-hidden group hover:border-indigo-500 transition-all hover:shadow-xl hover:-translate-y-1">
            <div className="h-40 bg-slate-50 relative overflow-hidden">
               <div className="absolute inset-0 flex items-center justify-center text-slate-200">
                 <Icons.Globe />
               </div>
               <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-3 backdrop-blur-[2px]">
                 <button 
                   onClick={() => onSelectSite(site)}
                   className="bg-white text-slate-900 px-6 py-2 rounded-xl text-sm font-bold hover:bg-indigo-50 transition-colors"
                 >
                   Open Editor
                 </button>
                 <button 
                   onClick={(e) => handleDelete(e, site)}
                   className="bg-rose-500 text-white p-2.5 rounded-xl hover:bg-rose-600 transition-colors"
                 >
                   <Icons.Trash />
                 </button>
               </div>
            </div>
            <div className="p-6">
              <h3 className="font-bold text-lg mb-1">{site.name}</h3>
              <p className="text-slate-400 text-xs font-mono">{site.domain}</p>
              <div className="flex items-center gap-2 mt-4">
                <div className="w-4 h-4 rounded-full border border-slate-100" style={{ backgroundColor: site.primaryColor }}></div>
                <div className="w-4 h-4 rounded-full border border-slate-100" style={{ backgroundColor: site.secondaryColor }}></div>
                <span className="text-[10px] text-slate-400 font-bold ml-auto uppercase tracking-tighter">{site.pages.length} Pages</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {isCreatorOpen && (
        <WebsiteCreator 
          onCancel={() => setIsCreatorOpen(false)} 
          onGenerate={handleCreate}
          isGenerating={false}
        />
      )}
    </div>
  );
};
